import { useEffect, useState, useMemo, useCallback } from 'react';
import { apiFetch, getToken, STREAM_API } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import { Report, Table, Stat, Hero, secs, sel } from '../../components/AnalyticsReport';

// ── Shop Analytics Report ─────────────────────────────────────────────────────
// The rolling output of the scheduled analyzer: footfall, time spent per
// customer, how long each post was manned, shelf reaches — each window rendered
// by the SAME component the upload analyzer uses (components/AnalyticsReport),
// because two renderers would mean two definitions of every figure.
//
// Two tiers, and the distinction is the whole point of this page:
//
//   * in / out / inside now  — live, available right now, counted in real time by
//     the counting line. Read it on the Footfall Report.
//   * dwell / staff time / shelf — produced by the scheduled run, so for today
//     they do not exist until tonight's analysis has happened.
//
// Defaulting to "today" and rendering an empty page at 11am would read as broken,
// so a day with no analysed window says WHY it is empty and when it is expected.
// A late number is fine; a number that looks complete and is not is not.
//
// Across a range, only the additive fields are summed. unique_customers is NOT
// summed: the same regular walks in every week, so adding them up and still
// calling them unique would be wrong. Across more than one window it is labelled
// visits.

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function IndustrialShopAnalyticsReportPage() {
  const [cams, setCams]     = useState([]);
  const [scope, setScope]   = useState(null);
  const [from, setFrom]     = useState(todayStr());
  const [to, setTo]         = useState(todayStr());
  const [days, setDays]     = useState([]);
  const [loading, setLoad]  = useState(false);
  const [lightbox, setLb]   = useState(null);
  const [token, setToken]   = useState('');

  useEffect(() => { getToken().then(t => setToken(t || '')).catch(() => {}); }, []);

  const [sched, setSched] = useState({});

  // Polled, not fetched once: a run takes an hour or more and the page is very
  // likely open across the moment it finishes. Without this the report sits
  // there looking empty long after the numbers exist.
  useEffect(() => {
    let alive = true;
    const tick = () => apiFetch('/ui/analytics/cameras').then(r => r.json())
      .then(d => {
        if (!alive) return;
        setCams(d.cameras || []);
        setSched(d.scheduler || {});
        if ((d.cameras || []).length) setScope(c => c ?? d.cameras[0].idx);
      }).catch(() => {});
    tick();
    const iv = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const load = useCallback(() => {
    if (scope == null) return;
    setLoad(true);
    apiFetch(`/ui/analytics/report?cam=${scope}&date_from=${from}&date_to=${to}`)
      .then(r => r.json())
      .then(d => setDays(d.days || []))
      .catch(() => setDays([]))
      .finally(() => setLoad(false));
  }, [scope, from, to]);

  useEffect(() => { load(); }, [load]);

  const cam = cams.find(c => c.idx === scope) || {};

  // Every analysed window in range, newest first.
  const windows = useMemo(() => {
    const out = [];
    (days || []).forEach(d => (d.windows || []).forEach(w => out.push({ ...w, day: d.day })));
    out.sort((a, b) => (b.from || '').localeCompare(a.from || ''));
    return out;
  }, [days]);

  // Totals across the range. Each field is combined the way that field can
  // honestly be combined, and nothing else is invented.
  const totals = useMemo(() => {
    if (!windows.length) return null;
    const t = { windows: windows.length, in: 0, out: 0, visits: 0, shelf_opens: 0,
                counter_secs: 0, peak: 0, dwell_num: 0, dwell_den: 0, footage: 0 };
    windows.forEach(w => {
      const s = w.results?.summary || {};
      t.in    += s.customers_in || 0;
      t.out   += s.customers_out || 0;
      t.visits += s.unique_customers || 0;
      t.shelf_opens += s.shelf_opens || 0;
      t.counter_secs += s.counter_secs_total || 0;
      t.peak = Math.max(t.peak, s.peak_concurrent || 0);
      t.footage += s.analysed_secs || 0;
      // Weighted by the number of customers the average came from, not a mean of
      // means — a window with one customer must not weigh as much as one with ten.
      const n = s.unique_customers || 0;
      t.dwell_num += (s.avg_dwell_secs || 0) * n;
      t.dwell_den += n;
    });
    t.avg_dwell = t.dwell_den ? t.dwell_num / t.dwell_den : 0;
    return t;
  }, [windows]);

  const single = windows.length === 1;
  const isToday = from === todayStr() && to === todayStr();
  // A run in flight. The stored watermark does not move until a window finishes,
  // so without this an hour-long run is indistinguishable from one that never
  // started — which is how it read on the first real install.
  const running = sched.running_cam === scope ? sched : null;
  const runningFor = running?.running_since
    ? Math.max(0, Math.round((Date.now() / 1000 - running.running_since) / 60)) : 0;

  const proofUrl = (jid) => (name) =>
    `${STREAM_API}/ui/analyze/${jid}/proof/${name}?token=${token}`;

  return (
    <IndustrialLayout title="Shop Analytics Report">
      {/* ── filters ──────────────────────────────────────────────────────── */}
      <div className="ind-card">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div className="ind-eye" style={{ marginBottom: 6 }}>Camera</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {cams.map(c => (
                <button key={c.idx} className={`ind-pill${scope === c.idx ? ' on' : ''}`}
                  onClick={() => setScope(c.idx)}>{c.name}</button>
              ))}
              {!cams.length &&
                <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                  No camera has scheduled analytics switched on yet — set one up on
                  Shop Analytics Config.
                </span>}
            </div>
          </div>
          <span style={{ flex: 1 }} />
          <div>
            <div className="ind-eye" style={{ marginBottom: 6 }}>From</div>
            <input type="date" value={from} style={sel} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <div className="ind-eye" style={{ marginBottom: 6 }}>To</div>
            <input type="date" value={to} style={sel} onChange={e => setTo(e.target.value)} />
          </div>
          <button className="ind-pill" onClick={() => { setFrom(todayStr()); setTo(todayStr()); }}>Today</button>
          <button className="ind-pill" onClick={() => {
            const d = new Date(); d.setDate(d.getDate() - 6);
            setFrom(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
            setTo(todayStr());
          }}>Last 7 days</button>
          <button className="ind-pill" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* ── what is live vs what is pending ──────────────────────────────── */}
      {scope != null && (
        <div className="ind-card">
          <span className="ind-eye">Where this camera is up to</span>
          <p style={{ fontSize: 12, color: 'var(--ind-tx2)', margin: '8px 0 0', lineHeight: 1.7 }}>
            Counting hours <b>{cam.window?.start}–{cam.window?.end}</b>, analysed
            at <b>{cam.run_at}</b>.{' '}
            {cam.last_window
              ? <>Last analysed window <code>{cam.last_window}</code>.</>
              : <>Nothing analysed yet.</>}
            {cam.last_error && <span style={{ color: 'var(--ind-warn, #f59e0b)' }}> Last attempt: {cam.last_error}</span>}
          </p>
          {running && (
            <p style={{ fontSize: 12, margin: '8px 0 0', lineHeight: 1.7,
              color: 'var(--ind-ok, #22c55e)' }}>
              <b>⏳ Analysing now</b> — window <code>{running.running_window}</code>,
              running {runningFor} min. The figures appear here when it finishes;
              this page checks every 20 seconds. Progress is on the Video Analyzer page.
            </p>
          )}
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '8px 0 0', lineHeight: 1.7 }}>
            <b>In / out / inside now are counted live</b> and are on the Footfall
            Report. The figures below — time spent per customer, time each post was
            manned, shelf reaches — come from the scheduled run, so today's appear
            once tonight's analysis has finished.
          </p>
        </div>
      )}

      {loading && <div className="ind-card" style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>Loading…</div>}

      {/* Empty is the normal state for "today" before the run. Say so, rather than
          showing a blank page that reads as a broken feature. */}
      {!loading && !windows.length && scope != null && (
        <div className="ind-card">
          <span className="ind-eye">Nothing analysed in this range</span>
          <p style={{ fontSize: 12, color: 'var(--ind-tx2)', marginTop: 8, lineHeight: 1.7 }}>
            {running
              ? <>The analysis is <b>running now</b> ({runningFor} min so far). These
                  figures appear the moment it finishes — nothing is wrong.</>
              : isToday
              ? <>Today's window is analysed at <b>{cam.run_at || 'the configured time'}</b>,
                  so these figures are not available yet. The live in / out tally is
                  on the Footfall Report in the meantime.</>

              : <>No window was analysed on these dates. Either analytics was off, the
                  camera was not recording those hours, or the recordings had already
                  aged out before the run.</>}
          </p>
        </div>
      )}

      {/* ── range totals, when more than one window is in view ───────────── */}
      {!loading && totals && !single && (
        <div className="ind-card">
          <span className="ind-eye">{from} to {to} — {totals.windows} analysed windows</span>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            {/* VISITS, not unique customers: the same regular walks in every week,
                and summing uniques across days would silently relabel them. */}
            <Hero label="CUSTOMER VISITS" val={totals.visits} />
            <Stat label="CAME IN"  val={totals.in}  color="var(--ind-ok)" />
            <Stat label="WENT OUT" val={totals.out} color="var(--ind-accent)" />
            <Stat label="AVG TIME SPENT" val={secs(totals.avg_dwell)} />
            <Stat label="BUSIEST MOMENT" val={totals.peak} />
            <Stat label="TIME BEHIND COUNTER" val={secs(totals.counter_secs)} color="#f59e0b" />
            {totals.shelf_opens > 0 &&
              <Stat label="SHELF OPENINGS" val={totals.shelf_opens} color="#facc15" />}
            <Stat label="FOOTAGE ANALYSED" val={secs(totals.footage)} color="var(--ind-tx3)" />
          </div>
          <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 10, lineHeight: 1.6 }}>
            Visits, not unique people — a customer who comes in on Monday and again
            on Friday is two visits. Within a single window the report below does say
            unique. Average time spent is weighted by how many customers each window
            saw, not an average of averages.
          </p>
          <Table head={['Day', 'Window', 'In', 'Out', 'Visits', 'Avg time', 'Counter time', 'Shelf']}
            rows={windows.map(w => {
              const s = w.results?.summary || {};
              return [w.day,
                `${(w.from || '').slice(9, 13)}–${(w.to || '').slice(9, 13)}`,
                s.customers_in ?? '—', s.customers_out ?? '—',
                s.unique_customers ?? '—', secs(s.avg_dwell_secs),
                secs(s.counter_secs_total), s.shelf_opens || '—'];
            })}
            empty="" />
        </div>
      )}

      {/* ── each window, rendered by the shared analyzer report ──────────── */}
      {!loading && windows.map(w => (
        <div key={w.window_key}>
          <div className="ind-card" style={{ paddingBottom: 8 }}>
            <span className="ind-eye">
              {w.day} · {(w.from || '').slice(9, 13)}–{(w.to || '').slice(9, 13)}
            </span>
            {w.proofs_purged && (
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '6px 0 0' }}>
                Proof photos for this window have passed their retention — the
                numbers remain, the images do not.
              </p>
            )}
          </div>
          {w.results?.summary
            ? <Report res={w.results} job={w.job_id ? { id: w.job_id } : null}
                token={token}
                proofUrl={w.job_id ? proofUrl(w.job_id) : () => ''}
                onProof={({ src, caption }) => w.job_id && setLb({ src, caption })} />
            : <div className="ind-card" style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                This window produced no results.
              </div>}
        </div>
      ))}

      {lightbox && (
        <div onClick={() => setLb(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 60,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
            gap: 10, padding: 20, cursor: 'zoom-out' }}>
          <img src={lightbox.src} alt="" style={{ maxWidth: '96vw', maxHeight: '86vh', borderRadius: 8 }} />
          <div style={{ fontSize: 12, color: '#cbd5e1' }}>{lightbox.caption}</div>
        </div>
      )}
    </IndustrialLayout>
  );
}
